/**
 * The UMED board as a Kanban (Isaiah Cotto, 2026-10-13): the operational kit
 * (components/board/kanban) over GET /umed/kanban.
 *
 * The server places every case; this file supplies the three things a
 * workspace owns -- what a card says, which drops open which governed act,
 * and how an act is opened -- and nothing else.
 *
 * == Drops (UMED_TRANSITIONS) =============================================
 *
 *   Packet ready -> Submitted to DCF      opens the sending chooser (focus 'choose-sending'). Never records.
 *   Ready to approve -> Approved          opens Approve with its own confirmation (focus 'approve'): Josie's act,
 *                                         gated on NOCA received + FLMMIS reflected, in her approval scope.
 *   Needs creation -> Packet ready        refused: the packet review and the DCF account move it; offers the packet.
 *   Submitted -> Ready to approve         refused: the evidence is recorded from the case; offers the evidence.
 *   anything -> With Finance, backwards   refused.
 *
 * Opening an act is the shell's own `onContinue(id, kind)`: the case opens in
 * the docked CaseContext with that focus, the same way the table's row acts
 * continue there. Every act decides again on the server.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OperationalKanban, LayoutSwitch, CardChips, CardStatus } from '../../components/board/kanban';
import type { KanbanColumnData, TransitionMap, ElsewhereBucket } from '../../components/board/kanban';
import { umedKanbanApi } from './umedKanbanApi';
import type { UmedKanbanCard, UmedKanbanPage, UmedColumnKey, UmedKanbanOwner, UmedKanbanSort, UmedElsewhere } from './umedKanbanApi';
import { ACTION_LABEL, CREATION_REASON_LABEL, UMED_TYPE_LABEL, CORRECTION_NOTE_WITHHELD, canDo, personName, shortDate } from './umedApi';
import type { CaseFocus, Scope } from './umedApi';
import BuildingOrClinic from '../../components/emerge/BuildingOrClinic';

/** What a drop's availability check is given: the viewer's capabilities (scope.can, writes NATIVE). */
export interface UmedKanbanCtx { canSend: boolean; canApprove: boolean }

const COLUMN_ORDER: UmedColumnKey[] = ['needs-creation', 'packet-ready', 'submitted', 'ready-to-approve', 'approved-finance-pending', 'with-finance', 'resubmittals'];
const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

// 2026-10-19: UMED starts one too -- from the case's status menu in the Table layout (REVERSAL / DCF RESUBMITTAL…), with its form and
// confirmation. A drop still writes nothing; the line says where the start is.
// 2026-10-21: the column also holds every correction recorded in eMerge (Corrections & resubmittals), so the words name both.
export const RESUBMITTAL_INTO_REFUSAL = 'A drop does not start a correction or a DCF resubmittal. Start one from the case’s status menu in the Table layout (SFS CORRECTION… or REVERSAL / DCF RESUBMITTAL…), or Finance starts a DCF resubmittal on its board.';
export const RESUBMITTAL_OUT_REFUSAL = 'Record the sending, DCF’s answer, the completed correction or the withdrawal on the case.';

export const UMED_TRANSITIONS: TransitionMap<UmedKanbanCard, UmedKanbanCtx> = {
  moves: {
    'packet-ready': {
      submitted: {
        kind: 'act', act: 'choose-sending', label: 'Record the sending…', opens: 'Opens the sending chooser in the case',
        available: (card, ctx) => (!card.acts.choose_sending
          ? 'The packet is not prepared for sending yet. Open the case to see what holds it.'
          : ctx.canSend ? true : 'Recording a sending needs the submission permission.'),
      },
    },
    'ready-to-approve': {
      'approved-finance-pending': {
        kind: 'act', act: 'approve', label: 'Approve…', opens: 'Opens Approve in the case, with its confirmation',
        available: (card, ctx) => (!card.acts.approve
          ? 'Approve needs NOCA received and FLMMIS reflected on record, in your approval scope.'
          : ctx.canApprove ? true : 'Approving needs the approval review permission.'),
      },
    },
    'needs-creation': {
      'packet-ready': {
        kind: 'refuse', reason: 'Review the packet and record the DCF account; the case moves itself.',
        instead: { label: 'Open the case at the packet', act: 'packet' },
      },
    },
    submitted: {
      'ready-to-approve': {
        kind: 'refuse', reason: 'Approval evidence is recorded from the case: NOCA and FLMMIS.',
        instead: { label: 'Open the case at the approval evidence', act: 'approval-evidence' },
      },
    },
  },
  fallbackReason: (from, to) => {
    // A DCF resubmittal is started by its form (UMED's status menu, or Finance's board), never by a drop; UMED records its
    // sending, DCF's answer or a withdrawal on the case.
    if (to === 'resubmittals') return RESUBMITTAL_INTO_REFUSAL;
    if (from === 'resubmittals') return RESUBMITTAL_OUT_REFUSAL;
    if (to === 'with-finance') return 'Finance takes a case from its own handoff; nothing moves into With Finance from the board.';
    const a = COLUMN_ORDER.indexOf(from as UmedColumnKey);
    const b = COLUMN_ORDER.indexOf(to as UmedColumnKey);
    if (b < a) return 'Cases do not move backward on the board. A correction, reversal or resubmission is recorded from the case.';
    if (to === 'approved-finance-pending') return 'Only a case in Ready to approve can be approved: NOCA received and FLMMIS reflected come first.';
    return 'No single act makes that move. Open the case to see its next step.';
  },
};

const DUE_WORDS = { OVERDUE: 'Overdue', TODAY: 'Due today', UPCOMING: 'Due' } as const;

/**
 * A due day on a card: "Oct 10" in the current year, "Oct 10, 2025" otherwise -- short enough that the next action
 * and its day share one line (verification 2026-10-14, K6). The full date is in the case.
 */
export function cardDay(iso: string, today: Date = new Date()): string {
  const full = shortDate(iso);
  const year = /^(\d{4})-/.exec(iso);
  return year && Number(year[1]) === today.getFullYear() ? full.replace(/, \d{4}$/, '') : full;
}

function ownerWords(card: UmedKanbanCard): { text: string; none: boolean } {
  const o = card.owner;
  if (o.kind === 'ASSIGNED' && o.name) return { text: o.name, none: false };
  if (o.kind === 'NO_ACCOUNT' && o.name) return { text: `${o.name} (no account)`, none: false };
  if (o.kind === 'UNRESOLVED') return { text: 'Owner not on the roster', none: true };
  return { text: card.claimable ? 'Unclaimed' : 'No owner', none: true };
}

/** What a UMED card says. Phrasing content only: the card's body is the button that opens the case. No money, ever. */
export function UmedCardBody({ card }: { card: UmedKanbanCard }) {
  // What is being sent, said only when it is not the ordinary new UMED (a correction, a reversal, a resubmission).
  const purpose = card.purpose && card.purpose !== 'NEW' ? CREATION_REASON_LABEL[card.purpose]
    : !card.purpose && card.umed_type && card.umed_type !== 'NEW' ? UMED_TYPE_LABEL[card.umed_type] : null;
  const owner = ownerWords(card);
  const next = card.next_action;
  return (
    <>
      <span className="okb-c-name">{personName(card)}</span>
      <span className="okb-c-sub" title={card.facility_name || undefined}><BuildingOrClinic row={card} fallback="Facility not recorded" /></span>
      <span className="okb-c-row">
        <CardStatus word={card.status.word} color={card.status.legend ? card.status.legend.color : null} emptyLabel="No status word" after={purpose} />
      </span>
      {next && (
        <span className="okb-c-row">
          <span className="okb-c-next">{ACTION_LABEL[next.action_type] || next.action_type}</span>
          {next.due_on ? (
            <span className={`okb-c-due${card.due === 'OVERDUE' ? ' okb-c-due--overdue' : card.due === 'TODAY' ? ' okb-c-due--today' : ''}`}>
              {card.due === 'TODAY' ? DUE_WORDS.TODAY : `${card.due ? DUE_WORDS[card.due] : 'Due'} ${cardDay(next.due_on)}`}
            </span>
          ) : <span className="okb-c-due">No due date</span>}
        </span>
      )}
      <CardChips chips={card.chips} />
      {/* The note for Gwynn the correction was asked with (2026-10-29): what she sends, on the card she works from. */}
      {card.correction?.note && (
        <span className="okb-c-note" data-testid="kanban-correction-note" title={card.correction.note}>
          <span className="okb-c-note__label">Note for Gwynn: </span>{card.correction.note}
        </span>
      )}
      {card.correction?.note_withheld && <span className="okb-c-note okb-c-note--withheld" data-testid="kanban-correction-note">{CORRECTION_NOTE_WITHHELD}</span>}
      <span className="okb-c-foot">
        <span className={`okb-c-owner${owner.none ? ' okb-c-owner--none' : ''}`}><span className="okb-sr">Owner: </span>{owner.text}</span>
      </span>
    </>
  );
}

/**
 * The open record at a glance, under its name in the dock's header (2026-10-30): the building, the status word with
 * what is being sent, the card's chips, and the owner -- the card's own words, nothing new.
 */
export function UmedDockMeta({ card }: { card: UmedKanbanCard }) {
  const purpose = card.purpose && card.purpose !== 'NEW' ? CREATION_REASON_LABEL[card.purpose]
    : !card.purpose && card.umed_type && card.umed_type !== 'NEW' ? UMED_TYPE_LABEL[card.umed_type] : null;
  const owner = ownerWords(card);
  return (
    <>
      <span className="board-dock__metaitem"><BuildingOrClinic row={card} fallback="Facility not recorded" /></span>
      <CardStatus word={card.status.word} color={card.status.legend ? card.status.legend.color : null} emptyLabel="No status word" after={purpose} />
      <CardChips chips={card.chips} />
      <span className={`board-dock__metaitem okb-c-owner${owner.none ? ' okb-c-owner--none' : ''}`}><span className="okb-sr">Owner: </span>{owner.text}</span>
    </>
  );
}

const SORT_OPTIONS: Array<{ value: '' | UmedKanbanSort; label: string }> = [
  { value: '', label: 'Each column’s own order' },
  { value: 'waiting', label: 'Waiting longest (sent to DCF)' },
  { value: 'created', label: 'Case created' },
  { value: 'resident', label: 'Resident' },
  { value: 'facility', label: 'Facility' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'sent-to-dcf', label: 'Sent to DCF' },
  { value: 'approved', label: 'Approved' },
];
const OWNER_OPTIONS: Array<{ value: '' | UmedKanbanOwner; label: string }> = [
  { value: '', label: 'Anyone' }, { value: 'mine', label: 'Mine' }, { value: 'unassigned', label: 'No owner' },
];
const isSort = (v: string | null): v is UmedKanbanSort => SORT_OPTIONS.some((o) => o.value && o.value === v);
const isOwner = (v: string | null): v is UmedKanbanOwner => v === 'mine' || v === 'unassigned';

export interface UmedKanbanProps {
  scope: Scope;
  q: string;
  owner: string | null;
  sort: string | null;
  dir: string | null;
  /** The toolbar's state lives in the URL; the shell writes it. */
  onQuery: (patch: { q?: string; owner?: string | null; sort?: string | null; dir?: string | null }) => void;
  selected: string | null;
  onSelect: (id: string | null) => void;
  /** Open the case with an act pre-targeted: the shell's askFocus + ?case= (UmedShell onContinue). */
  onContinue: (id: string, kind: CaseFocus['kind']) => void;
  /** The docked record: CaseContext, as the shell renders it. */
  detail: React.ReactNode;
  /** An act recorded in the case panel: read the board again (a fresh n per act). */
  changed?: { n: number; msg: string; caseId: string | null } | null;
  /** The toolbar's Kanban | Table switch. Omitted (2026-10-14): the workspace sidebar's Views group chooses the layout. */
  onLayout?: (layout: 'kanban' | 'table') => void;
  /** Where an Elsewhere bucket is worked. */
  elsewhereLink: (e: UmedElsewhere) => { href: string; go: () => void } | null;
}

type Columns = Array<KanbanColumnData<UmedKanbanCard> & { page: number }>;

export default function UmedKanban({ scope, q, owner, sort, dir, onQuery, selected, onSelect, onContinue, detail, changed = null, onLayout, elsewhereLink }: UmedKanbanProps) {
  const ownerKey = isOwner(owner) ? owner : null;
  const sortKey = isSort(sort) ? sort : null;
  const sortDir: 'asc' | 'desc' = dir === 'desc' ? 'desc' : 'asc';
  const [page, setPage] = useState<UmedKanbanPage | null>(null);
  const [columns, setColumns] = useState<Columns>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const inFlight = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    if (inFlight.current) inFlight.current.abort();
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    inFlight.current = ctl;
    const stale = () => inFlight.current !== ctl;
    setLoading(true); setErr(null);
    try {
      const r = await umedKanbanApi.read({ q, owner: ownerKey, sort: sortKey, dir: sortDir }, ctl ? ctl.signal : undefined);
      if (stale()) return;
      if (r.success && r.data) {
        setPage(r.data);
        setColumns(r.data.columns.map((c) => ({ key: c.key, label: c.label, what: c.what, count: c.count, cards: c.cards, has_more: c.has_more, page: c.page })));
      } else { setErr(r.error || r.message || 'The server refused the board.'); }
    } catch { if (!stale()) setErr('Could not reach the server.'); } finally { if (!stale()) setLoading(false); }
  }, [q, ownerKey, sortKey, sortDir]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (inFlight.current) inFlight.current.abort(); }, []);
  const seen = useRef(changed ? changed.n : 0);
  useEffect(() => {
    if (!changed || changed.n === seen.current) return;
    seen.current = changed.n;
    load();
  }, [changed, load]);

  const loadMore = useCallback(async (key: string) => {
    const col = columns.find((c) => c.key === key);
    if (!col) return;
    setColumns((cs) => cs.map((c) => (c.key === key ? { ...c, loading_more: true } : c)));
    try {
      const r = await umedKanbanApi.read({ q, owner: ownerKey, sort: sortKey, dir: sortDir, column: key as UmedColumnKey, page: col.page + 1 });
      const got = r.success && r.data ? r.data.columns[0] : null;
      setColumns((cs) => cs.map((c) => {
        if (c.key !== key) return c;
        if (!got) return { ...c, loading_more: false };
        const have = new Set(c.cards.map((k) => k.id));
        return { ...c, loading_more: false, count: got.count, has_more: got.has_more, page: got.page, cards: [...c.cards, ...got.cards.filter((k) => !have.has(k.id))] };
      }));
      if (!got) setErr(r.error || r.message || 'The next cards could not be read.');
    } catch {
      setColumns((cs) => cs.map((c) => (c.key === key ? { ...c, loading_more: false } : c)));
      setErr('Could not reach the server.');
    }
  }, [columns, q, ownerKey, sortKey, sortDir]);

  const ctx: UmedKanbanCtx = useMemo(() => ({ canSend: canDo(scope, 'prepare_submission'), canApprove: canDo(scope, 'review_approval') }), [scope]);
  const onAct = useCallback((card: UmedKanbanCard, act: string) => {
    onContinue(card.id, act as CaseFocus['kind']);
  }, [onContinue]);

  const buckets: ElsewhereBucket[] = (page?.elsewhere || []).map((e) => {
    const link = elsewhereLink(e);
    // The chip's title says what its number is (verification 2026-10-14: 181 here, 233 on the tab it opens).
    const hint = e.key === 'approved-at-source' ? 'Open in Table to record a reversal or resubmittal.' : null;
    const what = [e.what, hint, page?.elsewhere_basis].filter(Boolean).join(' ');
    return { key: e.key, label: e.label, count: e.count, what, ...(link ? { href: link.href, onSelect: link.go } : {}) };
  });
  const total = page && typeof page.total === 'number' ? page.total : null;
  const nf = new Intl.NumberFormat('en-US');

  return (
    <OperationalKanban<UmedKanbanCard, UmedKanbanCtx>
      label="UMED board"
      idBase="um-kanban"
      unit={['case', 'cases']}
      columns={columns}
      renderCard={(card) => <UmedCardBody card={card} />}
      cardName={(card) => personName(card)}
      transitions={UMED_TRANSITIONS}
      ctx={ctx}
      onAct={onAct}
      onLoadMore={loadMore}
      loading={loading}
      error={err && !columns.length ? err : null}
      onRetry={load}
      notice={err && columns.length ? err : null}
      emptyText={q ? `Nothing here matches “${q}”.` : 'Nothing here.'}
      toolbar={{
        search: { value: q, onChange: (next) => onQuery({ q: next }), placeholder: 'Resident, facility or case reference', label: 'Search the board by resident, facility or case reference' },
        filters: [{ key: 'owner', label: 'Owner', value: ownerKey || '', options: OWNER_OPTIONS, onChange: (v) => onQuery({ owner: v || null }) }],
        sort: { value: sortKey || '', dir: sortDir, options: SORT_OPTIONS, onChange: (v, d) => onQuery({ sort: v || null, dir: v && d === 'desc' ? 'desc' : null }) },
        summary: total !== null ? <>{nf.format(total)} on the board{page && page.elsewhere_total ? ` · ${nf.format(page.elsewhere_total)} elsewhere` : ''}</> : null,
        viewSwitch: onLayout ? <LayoutSwitch value="kanban" onChange={onLayout} /> : undefined,
      }}
      elsewhere={{ label: 'Elsewhere', buckets, note: buckets.length && page?.elsewhere_basis ? page.elsewhere_basis : undefined }}
      detail={{
        openId: selected,
        onOpen: (id) => onSelect(id),
        onClose: () => onSelect(null),
        memoryKey: 'umed-kanban',
        label: (card) => (card ? `${personName(card)}: the case` : 'The open case'),
        title: (card) => (card ? <strong>{personName(card)}</strong> : <strong>Case</strong>),
        meta: (card) => (card ? <UmedDockMeta card={card} /> : null),
        render: () => detail,
      }}
    />
  );
}
